import { toImmutable, Immutable } from 'nuclear-js';
import Reactor from 'data/reactor';
import Getters from 'data/getters';
import DataUtils from 'data/utils';
import CommonValues from 'resources/common-values';

export const displayPageNames = {
    instructionalAreas: 'instructional-areas',
    studentProfile: 'profile',
    comparisons: 'comparisons',
    growthGoals: 'growth-goals',
};

export const displayPages = toImmutable([
    {
        name: displayPageNames.studentProfile,
        summaryDescription: 'Student Profile',
        displayLabel: 'Student Profile overview',
    },
    {
        name: displayPageNames.comparisons,
        summaryDescription: 'Comparisons',
        displayLabel: 'Comparisons',
    },
    {
        name: displayPageNames.instructionalAreas,
        summaryDescription: 'Instructional Areas',
        displayLabel: 'Instructional Areas (learning statements)',
    },
    {
        name: displayPageNames.growthGoals,
        summaryDescription: 'Growth Goals',
        displayLabel: 'Growth Goals (upcoming and previous)',
    },
]);

export function getSelectedTermValue(terms, selectedTerm) {
    const term = terms ? terms.find((t) => t.get('termNumber') === selectedTerm) : null;
    return term ? term.get('termNumber') : '';
}

export function selectedSubjectsBid(selectedSubjectBidsForPrinting, subjectActive, subjectsChanged) {
    let subjectContent = [];
    if (subjectsChanged && selectedSubjectBidsForPrinting.size > 0) {
        CommonValues.subjects.forEach((subject) => {
            if (selectedSubjectBidsForPrinting.includes(subject.get('bid'))) {
                subjectContent.push(subject.get('bid'));
            }
        });
    } else if (!subjectsChanged) {
        subjectContent.push(subjectActive);
    }
    return subjectContent;
}

export function selectedStudentsBid(
    option,
    studentInfo,
    selectedStudents,
    students,
    selectedStudentBids,
    selectedTerm
) {
    let studentContent = [];
    let selectedStudentInfo = null;

    if (selectedTerm == CommonValues.mostRecentTermNumber) {
        selectedStudentInfo = DataUtils.latestItemByTerm(studentInfo);
    } else {
        selectedStudentInfo = studentInfo
            ? studentInfo.find((si) => si.get('termNumber', '') == selectedTerm, null, null)
            : null;
    }
    const currentStudentBid = selectedStudentInfo ? selectedStudentInfo.get('studentBid') : '';

    if (option == 'current') {
        studentContent.push({ studentBid: currentStudentBid });
    } else if (option == 'all') {
        studentContent.push(); //By default it will have all students from selected classBid
    } else if (option == 'choose') {
        if (
            (selectedStudentBids != null && selectedStudentBids.size == 1) ||
            (selectedStudentBids.size > 1 && selectedStudents.size != students.size)
        ) {
            selectedStudentBids.forEach(function(studentBid) {
                studentContent.push({ studentBid: studentBid });
            });
        } else if (selectedStudents.size == students.size) {
            studentContent.push(); //By default it will have all students from selected classBid
        }
    }
    return studentContent;
}

export function selectedTestOption(option, testOption) {
    let noTestOption = false;
    if (option == 'all') {
        noTestOption = !!testOption.includes('no test');
    } else if (option == 'choose') {
        noTestOption = !!testOption.includes('no test selected students');
    }
    return noTestOption;
}

export function selectedPrintFormat(option, printFormat) {
    let printFormatOption = false;
    if (option == 'all') {
        printFormatOption = !!printFormat.includes('page break');
    } else if (option == 'choose') {
        printFormatOption = !!printFormat.includes('page break selected students');
    }
    return printFormatOption;
}

export function constructBatchPrintPayload(state, props) {
    const dummyUUID = '00000000-0000-1000-a000-000000000000';
    /*
     * Since this function is heavily coupled to the state and prop structure of the calling component,
     * We're going to break the needed vars out so it's easier to fix in the future.
     */
    const terms = props.termsWithTestEvents;
    const studentsParam = props.students;
    const selectedTerm = props.selectedTermNumber;

    const selectedSubject = props.selectedSubject;
    const selectedModule = props.selectedModule ? props.selectedModule : 'profile';
    const selectedStudents = state.selectedStudents;
    const selectedStudentBids = state.selectedStudentBids;
    const selectedPrintFormatParam = props.selectedPrintFormat;
    const selectedSubjectBidsForPrinting = state.selectedSubjectBidsForPrinting;
    const selectedStudentForPrinting = props.selectedStudentForPrinting;
    const subjectsChanged = state.subjectsChanged;
    const pagesChanged = state.pagesChanged;
    const selectedDataForPrinting = state.selectedDataForPrinting ? state.selectedDataForPrinting : toImmutable([]);
    const selectedPagesForPrinting = state.selectedPagesForPrinting;
    const selectedGroupByOption = state.selectedGroupByOption;
    const studentInfo = props.studentInfo;
    const isSingleStudentSearchRendering = props.isSingleStudentMode == true; // Quick fix for TP-227336
    const selectedClassBid = Reactor.evaluate(Getters.roster.selectedClassBid);
    const selectedSchoolBid = Reactor.evaluate(Getters.roster.selectedSchoolBid);
    let selectedTermBid = Reactor.evaluate(Getters.roster.selectedTermBid);
    const selectedInstructorBid = Reactor.evaluate(Getters.roster.selectedInstructorBid);

    let selectedTermValue = getSelectedTermValue(terms, selectedTerm);
    if (isSingleStudentSearchRendering && selectedTermValue === '') {
        // If we came from CPR UI and didn't find the term it supplied
        // revert to the worst case scenario of 900001
        selectedTermValue = '900001';
    }
    if (!selectedTermBid && isSingleStudentSearchRendering && selectedTermValue != '900001') {
        // When coming from CPR-UI a unique term number may have been selected
        // This will find it by looking through the terms for the supplied termNumber
        // then grabbing the termBid from it

        const selectedTermItem = terms.find((term) => term.get('termNumber') === selectedTerm);
        selectedTermBid = selectedTermItem ? selectedTermItem.get('businessIdentifier') : null;
    }

    if (selectedTermValue === '') {
        selectedTermValue = '900001';
    }

    const userInfo = Reactor.evaluate(Getters.user.userInfo);

    const subjectBid = selectedSubject ? selectedSubject.get('bid') : '';

    const subjects = selectedSubjectsBid(selectedSubjectBidsForPrinting, subjectBid, subjectsChanged);
    const projectedProficiencies = selectedDataForPrinting.toArray().map((ls) => ls.get('study'));
    const pages = pagesChanged ? selectedPagesForPrinting : Immutable.Set.of(selectedModule);
    const pagesRequest = props.isFamilyReport
        ? { familyReport: true }
        : {
              overview: !!pages.includes(displayPageNames.studentProfile),
              growthGoals: !!pages.includes(displayPageNames.growthGoals),
              instructionalAreas: pages.includes(displayPageNames.instructionalAreas) ? selectedGroupByOption : false,
              comparisons: pages.includes(displayPageNames.comparisons)
                  ? Reactor.evaluate(Getters.runtime.selectedQuadrantChartRotation)
                  : false,
          };
    const students = selectedStudentsBid(
        selectedStudentForPrinting,
        studentInfo,
        selectedStudents,
        studentsParam,
        selectedStudentBids,
        selectedTerm
    );

    const testOption = selectedTestOption(selectedStudentForPrinting, selectedPrintFormatParam);
    const printFormat = selectedPrintFormat(selectedStudentForPrinting, selectedPrintFormatParam);

    // Sample object structure
    //
    // "students":                      [{"studentBid":"123-abc-xyz-45"}],
    // "classBid":                      "123-abc-45-xyz",
    // "schoolBid":                     "123-abc-45-xyz",
    // "termBid":                       "123-abc-45-xyz",
    // "instructorBid":                 "123-abc-xyz-45",
    // "termNumber":                    900001,
    // "location":                      "S3",
    // "userName":                      "CNorris",
    // "partnerBid":                    "123-abc-xyz-45",
    // "userDisplayName":               "Chuck Norris",
    // "partnerId":                     123,
    // "educatorBid":                   "123-abc-xyz-45",
    // "subjects":                      [1,2]
    // "projectedProficiencies"         ["SAT", "ACT"]
    // "pages":                         {"overview":true/false, "growthGoals":true/false, "instructionalAreas":"topic/standard/false", "comparisons":"default/rotated/false"},
    // "includeNoTestStudents":         true/false,
    // "insertPageBreak":               true/false
    // "isSingleStudentSearchRendering" true/false

    return {
        students: students,
        classBid: !selectedClassBid && isSingleStudentSearchRendering ? dummyUUID : selectedClassBid,
        schoolBid: !selectedSchoolBid && isSingleStudentSearchRendering ? dummyUUID : selectedSchoolBid,
        termBid: !selectedTermBid && isSingleStudentSearchRendering ? dummyUUID : selectedTermBid,
        instructorBid: !selectedInstructorBid && isSingleStudentSearchRendering ? dummyUUID : selectedInstructorBid,
        termNumber: selectedTermValue,
        location: 'S3',
        userName: userInfo.get('username'),
        userDisplayName: `${userInfo.get('firstName')} ${userInfo.get('lastName')}`,
        partnerBid: userInfo.get('partnerBid'),
        partnerId: userInfo.get('partnerId'),
        educatorBid: userInfo.get('userBid'),
        subjects: subjects,
        projectedProficiencies: projectedProficiencies,
        pages: pagesRequest,
        includeNoTestStudents: testOption,
        insertPageBreak: printFormat,
        isSingleStudentSearchRendering: isSingleStudentSearchRendering,
    };
}

export default {
    getSelectedTermValue,
    selectedSubjectsBid,
    selectedStudentsBid,
    constructBatchPrintPayload,
    selectedTestOption,
    selectedPrintFormat,
    displayPages,
    displayPageNames,
};
